
// @ts-ignore
import { codemirror } from "vue-codemirror";
import "codemirror/lib/codemirror.css";
import "codemirror/theme/darcula.css";
import "codemirror/mode/css/css.js";
import "codemirror/mode/xml/xml.js";
import "codemirror/mode/twig/twig.js";
import "codemirror/mode/javascript/javascript.js";
import "codemirror/addon/display/placeholder.js";
import "codemirror/mode/php/php.js";
import "codemirror/addon/hint/show-hint.js";
import "codemirror/addon/hint/show-hint.css";
import "codemirror/addon/display/autorefresh.js";

import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { CodeInputLanguage } from "@/components/Reusable/CustomInputs/CodeInput/CodeInput.vue";
import { Editor, EditorChange } from "codemirror";
import { uiStore } from "@/store/store";

@Component({
  components: { codemirror }
})
export default class CodeMirrorWrapper extends Vue {
  @Prop()
  value!: string | null;

  @Prop()
  autofillOnEmpty!: string | null;

  @Prop()
  language!: CodeInputLanguage;

  @Prop({ default: true })
  showLineNumbers!: boolean;

  @Prop({ default: 1 })
  firstLineNumber!: number;

  @Prop({ default: false })
  readOnly!: boolean;

  @Prop({ default: "" })
  placeholder!: string;

  get options() {
    return {
      tabSize: 4,
      mode: this.language === "twig" ? "twig" : `text/${this.language}`,
      theme: "darcula",
      lineNumbers: this.showLineNumbers,
      firstLineNumber: this.firstLineNumber,
      readOnly: this.readOnly,
      lineWrapping: !uiStore.settings.disableCodeMirrorLineWrapping,
      // https://github.com/surmon-china/vue-codemirror/issues/61#issuecomment-663078357
      autoRefresh: { delay: 50 }
    };
  }

  additionalEventsToListenFromInternalCodeMirrorInstance = ["change", "focus"];

  get classList() {
    return {
      "breakdance-codemirror": true,
      "breakdance-codemirror-readonly": this.readOnly
    };
  }

  handleInternalCmChange(codemirrorInstance: Editor, change: EditorChange) {
    // In order to preserve single-directional data flow we should ignore
    // change events originated from Vue reactivity
    if (change.origin !== "setValue") {
      this.$emit("input", codemirrorInstance.getValue());
    }
  }

  handleInternalCmFocus(codemirrorInstance: Editor) {
    this.handleAutofillOnEmpty(codemirrorInstance);
  }

  handleCmInputRead(codemirrorInstance: Editor) {
    this.$emit("inputRead", codemirrorInstance);
  }

  handleCmReady(codemirrorInstance: Editor) {
    this.$emit("ready", codemirrorInstance);

    if (uiStore.keyboardTriggeredActions.focusCodeMirror === "pending") {
      this.$nextTick(() => {
        codemirrorInstance.focus();
      });
      uiStore.setKeyboardTriggeredActions({
        ...uiStore.keyboardTriggeredActions,
        focusCodeMirror: null
      });
    }
  }

  handleAutofillOnEmpty(codemirrorInstance: Editor) {
    if (!this.value && this.autofillOnEmpty) {
      const lineAndPos = getLineAndPositionOfString(
        "PLACECURSORHERE",
        this.autofillOnEmpty
      );
      codemirrorInstance.setValue(
        this.autofillOnEmpty.replace("PLACECURSORHERE", "")
      );
      codemirrorInstance.setCursor(lineAndPos);
    }
  }
}

function getLineAndPositionOfString(needle: string, haystack: string) {
  let retVal = { line: 0, ch: 0 };
  const lines = haystack.split("\n");
  lines.forEach((line, lineNumber) => {
    const pos = line.indexOf(needle);
    if (pos !== -1) {
      retVal = { line: lineNumber, ch: pos };
    }
  });
  return retVal;
}
